import { isMapStyleReady } from '../mapcore.js';
// Layer marker "mare & extra" v2 — port fedele dei layer marker della v1:
//   boe        → WSWeatherApi.getBuoys()                (map-markers.js:178-352)
//   ndbcShips  → WSWeatherApi.getNdbcShipObservations() (map-markers.js:126-175)
//   ascat      → /assets/data/ascat/sicily.json         (map-derived-layers.js:603-732)
//   s1wind     → /assets/data/sentinel1/sicily.json     (map-derived-layers.js:734-823)
//   webcam     → WSWeatherApi.getWebcams()              (map-webcams.js)
//   mobile     → WSWeatherApi.getMobileStations()       (map-markers.js:2948-3053, MeteoTracker)
//
// Pattern stations.js/station-sprites.js: divIcon v1 replicati come sprite
// canvas → map.addImage({pixelRatio}) con ancora ricentrata + symbol layer;
// popup/tooltip con l'HTML v1 ESATTO (CSS v1 portato in un <style> iniettato,
// selettori shell adattati Leaflet→MapLibre, valori identici).
// Frecce ASCAT/Sentinel-1: icon-rotate data-driven su sprite per (colore, len).
//
// Refresh dati live: la v1 ridisegna boe/ndbcShips/mobile/webcam dentro
// refreshAll() ogni 60s con guard document.hidden + refresh immediato al
// ritorno di visibilità (map.js:1442-1462) → cadenza portata identica.
//
// Globali richiesti: chroma (vendor), window.WSPalettes, window.WSWeatherApi.
import { finiteValue, validStationValue } from '../lib/params.js';
import { getColorScale } from '../lib/scales.js';
import { layoutWebcamPoints } from '../lib/webcam-layout.js';
import { pickTextAndStroke, stationSpriteDpr } from './station-sprites.js';
import { state as stationsState } from './stations.js';

const REFRESH_MS = 60000; // v1: setInterval(refreshAll, 60000) (map.js:1455)
const LIVE_KEYS = ['boe', 'ndbcShips', 'mobile', 'webcam'];

const ASCAT_URL = '/assets/data/ascat/sicily.json';
const S1_URL = '/assets/data/sentinel1/sicily.json';
const CAM_SNAPSHOT_BASE = 'https://cam.weathersicily.it/api/snapshot/';

// Declutter boe⟷stazioni (v1 map-markers.js:266-274, scelta utente 2026-07-01)
const WS_BOE_DECLUTTER_MIN_ZOOM = 0;
const WS_BOE_OVERLAP_PX = 46;
const WS_BOE_OFFSET_PX = 62; // 30 in v1: copriva il valore della stazione (feedback Giuseppe)

// ---------------------------------------------------------------------------
// Scale colori (identiche v1 map-legend.js:1659-1664: delega a WSPalettes)
// ---------------------------------------------------------------------------
const seaTempScale = (v) => window.WSPalettes.scale('temp_mare')(v);
const liveTemperatureScale = (v) => getColorScale('temperatura')(v);

// Scala intensità vento mare ASCAT (m/s) — v1 map-derived-layers.js:613-622
const ASCAT_SCALE = [
    { max: 3,   fill: '#74c0fc', label: '< 3' },
    { max: 6,   fill: '#3bc9db', label: '3–6' },
    { max: 9,   fill: '#51cf66', label: '6–9' },
    { max: 12,  fill: '#fcc419', label: '9–12' },
    { max: 15,  fill: '#ff922b', label: '12–15' },
    { max: 19,  fill: '#fa5252', label: '15–19' },
    { max: 1e9, fill: '#ae3ec9', label: '> 19' },
];
function ascatColor(ms) { return (ASCAT_SCALE.find(s => ms < s.max) || ASCAT_SCALE[ASCAT_SCALE.length - 1]).fill; }
// (dedup S1 su griglia 0.05° è fatta lato server da ws-sentinel1-wind: qui i
// nodi arrivano già mosaicati, v1 map-derived-layers.js:743+768)

// ---------------------------------------------------------------------------
// Helper generici (port 1:1 da map-markers.js / map-layers.js)
// ---------------------------------------------------------------------------
function esc(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
    }[ch]));
}

function sanitizeHttpUrl(value) {
    if (!value) return '';
    try {
        const url = new URL(String(value), window.location.href);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (e) {
        return '';
    }
}

function cleanWindDirection(value) {
    const raw = String(value ?? '').trim().toUpperCase();
    if (!raw || raw === '-' || raw === '-1' || raw === 'NULL' || raw === 'NAN') return '';
    const validCompass = new Set([
        'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
        'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
    ]);
    if (validCompass.has(raw)) return raw;
    const degrees = finiteValue(raw);
    if (degrees !== null && degrees >= 0 && degrees <= 360) {
        return `${Math.round(degrees)}°`;
    }
    return '';
}

function wsMarineMetric(label, value, unit, decimals = 1) {
    const n = finiteValue(value);
    if (n === null) return '';
    const formatted = n.toFixed(decimals).replace(/\.0$/, '');
    return `<div class="ws-ndbc-popup__metric"><span>${esc(label)}</span><strong>${esc(formatted + (unit ? ' ' + unit : ''))}</strong></div>`;
}

function wsMarineMetricText(value, unit, decimals = 1) {
    const n = finiteValue(value);
    if (n === null) return '';
    return n.toFixed(decimals).replace(/\.0$/, '') + (unit ? ' ' + unit : '');
}

const IT_DDMM_HHMM = { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };

function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

function makeCanvas(w, h) {
    const dpr = stationSpriteDpr();
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineJoin = 'round';
    return { canvas, ctx, dpr };
}

// Sprite {canvas,w,h,ax,ay,dpr} → immagine MapLibre con ancora ricentrata
// (stesso trucco di stations.js: icon-anchor 'center' cade sull'ancora vera).
function centeredImage(sprite) {
    const { canvas, w, h, ax, ay, dpr } = sprite;
    const W = 2 * Math.max(ax, w - ax);
    const H = 2 * Math.max(ay, h - ay);
    const c = document.createElement('canvas');
    c.width = Math.ceil(W * dpr);
    c.height = Math.ceil(H * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(canvas, W / 2 - ax, H / 2 - ay, w, h);
    return { data: ctx.getImageData(0, 0, c.width, c.height), pixelRatio: dpr };
}

function addImageOnce(map, id, build) {
    if (map.hasImage(id)) return;
    const img = centeredImage(build());
    map.addImage(id, img.data, { pixelRatio: img.pixelRatio });
}

// ---------------------------------------------------------------------------
// Sprite canvas (replica dei divIcon v1)
// ---------------------------------------------------------------------------

// BOA — replica del divIcon SVG 50×34 (map-markers.js:215-237): rounded rect
// colorata temp mare, onda decorativa, valore .1 con halo paint-order:stroke.
function buildBuoySprite(tempText, color) {
    const w = 50, h = 34, cx = w / 2, cy = h / 2;
    const { fill: textColor, stroke: textStroke } = pickTextAndStroke(color);
    const { canvas, ctx, dpr } = makeCanvas(w, h);
    roundedRect(ctx, 1.5, 3, w - 3, h - 6, 12);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = 'rgba(255,255,255,0.92)';
    ctx.stroke();
    // onda: path v1 "M10 26 C14 23, 18 29, 22 26 S30 23, 34 26 S42 29, 45 26"
    // (le S riflettono il control point precedente → bezier espanse a mano)
    ctx.beginPath();
    ctx.moveTo(10, h - 8);
    ctx.bezierCurveTo(14, h - 11, 18, h - 5, 22, h - 8);
    ctx.bezierCurveTo(26, h - 11, 30, h - 11, 34, h - 8);
    ctx.bezierCurveTo(38, h - 5, 42, h - 5, 45, h - 8);
    ctx.lineCap = 'round';
    ctx.lineWidth = 1.3;
    ctx.strokeStyle = textColor;
    ctx.globalAlpha = 0.52;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.font = '700 13px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = textStroke;
    ctx.strokeText(tempText, cx, cy + 4);
    ctx.fillStyle = textColor;
    ctx.fillText(tempText, cx, cy + 4);
    return { canvas, w, h, ax: cx, ay: cy, dpr };
}

// NAVE NDBC — replica del divIcon 50×58 (map-markers.js:149-166 + CSS
// .ws-ndbc-ship-marker): pin colorato (color-mix 92%+8% bianco) con barchetta
// bianca dentro, pillola scura col valore sovrapposta in basso (-12px).
const NDBC_PIN_PATH = 'M17 2.5c7.6 0 13.7 5.8 13.7 13 0 9.7-13.7 16-13.7 16S3.3 25.2 3.3 15.5c0-7.2 6.1-13 13.7-13Z';
const NDBC_GLYPH_PATHS = [
    'M9.5 18.2h15l-2.1 4.2a3.5 3.5 0 0 1-3.1 1.9h-4.6a3.5 3.5 0 0 1-3.1-1.9l-2.1-4.2Z',
    'M13 10.8h7.1l2 7.4H11.4l1.6-7.4Z',
    'M16.8 10.8V7.2h3.4',
    'M9.4 24.9c1.7 0 1.7-.8 3.4-.8s1.7.8 3.4.8 1.7-.8 3.4-.8 1.7.8 3.4.8',
];
function buildNdbcShipSprite(label, color) {
    const glyph = 50, scale = glyph / 34; // viewBox 34 → 50px CSS
    const pillFont = '850 11px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    const probe = makeCanvas(1, 1).ctx;
    probe.font = pillFont;
    const pillW = Math.max(40, Math.ceil(probe.measureText(label).width) + 14); // min-width 40, padding 4px 7px 3px
    const pillH = 19;
    const w = Math.max(glyph, pillW);
    const h = 58;
    const { canvas, ctx, dpr } = makeCanvas(w, h);

    ctx.save();
    ctx.translate((w - glyph) / 2, 0);
    ctx.scale(scale, scale);
    // pin: fill = color-mix(in srgb, color 92%, #fff 8%)
    const pin = new Path2D(NDBC_PIN_PATH);
    ctx.fillStyle = chroma.mix(color, '#ffffff', 0.08, 'rgb').hex();
    ctx.fill(pin);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(255,255,255,0.96)';
    ctx.stroke(pin);
    // barchetta + albero + onda: stroke bianco 1.9, round
    ctx.lineWidth = 1.9;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#fff';
    NDBC_GLYPH_PATHS.forEach(d => ctx.stroke(new Path2D(d)));
    ctx.restore();

    // pillola valore (strong: margin-top -12 → top a 50-12=38)
    const pillTop = glyph - 12;
    roundedRect(ctx, w / 2 - pillW / 2, pillTop, pillW, pillH, pillH / 2);
    ctx.fillStyle = 'rgba(7,19,29,0.86)';
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.88)';
    ctx.stroke();
    ctx.font = pillFont;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#fff';
    ctx.fillText(label, w / 2, pillTop + pillH / 2 + 0.5);
    // iconAnchor v1: [25, 54] su 50×58
    return { canvas, w, h, ax: w / 2, ay: 54, dpr };
}

// FRECCIA VENTO MARE — replica ascatArrowIcon (map-derived-layers.js:628-639):
// svg 22×22, asta lunga len=min(11, 5+ms*0.55), punta piena. La rotazione qui
// NON è nel canvas: la fa icon-rotate data-driven (allineamento 'map').
function buildArrowSprite(color, len) {
    const w = 22, h = 22, c = 11;
    const { canvas, ctx, dpr } = makeCanvas(w, h);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.1;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(c, c + len);
    ctx.lineTo(c, c - len);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(c - 3.4, c - len + 3.6);
    ctx.lineTo(c, c - len);
    ctx.lineTo(c + 3.4, c - len + 3.6);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    return { canvas, w, h, ax: c, ay: c, dpr };
}

// Anteprima panoramica leggibile, con conteggio delle webcam raggruppate.
function buildWebcamThumbSprite(img, count = 1) {
    const w = 72, h = 46;
    const { canvas, ctx, dpr } = makeCanvas(w, h);
    roundedRect(ctx, 0, 0, w, h, 12);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.save();
    roundedRect(ctx, 0, 0, w, h, 12);
    ctx.clip();
    // object-fit: cover
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    const s = Math.max(w / iw, h / ih);
    const sw = w / s, sh = h / s;
    ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, w, h);
    ctx.restore();
    roundedRect(ctx, 1, 1, w - 2, h - 2, 11);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(255,255,255,0.65)';
    ctx.stroke();
    if (count > 1) {
        ctx.fillStyle = '#075985';
        ctx.fillRect(w - 26, h - 20, 26, 20);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(count), w - 13, h - 5);
    }
    return { canvas, w, h, ax: w / 2, ay: h / 2, dpr };
}

// WEBCAM (fallback senza thumb) — replica dell'SVG 34×34 (map-webcams.js:146-156).
function buildWebcamGlyphSprite() {
    const w = 34, h = 34;
    const { canvas, ctx, dpr } = makeCanvas(w, h);
    ctx.beginPath();
    ctx.arc(17, 17, 15, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.78)';
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#ffb74d';
    ctx.stroke();
    roundedRect(ctx, 10, 13, 14, 10, 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.beginPath(); ctx.arc(17, 18, 4, 0, Math.PI * 2); ctx.fillStyle = '#222'; ctx.fill();
    ctx.beginPath(); ctx.arc(17, 18, 2.2, 0, Math.PI * 2); ctx.fillStyle = '#ffb74d'; ctx.fill();
    return { canvas, w, h, ax: 17, ay: 17, dpr };
}

// METEOTRACKER — replica del divIcon SVG 40×40 (map-markers.js:3005-3022):
// quadrato arrotondato rx6 colorato temp, valore .1 con halo.
function buildMobileSprite(tempText, color) {
    const size = 40, center = size / 2;
    const { fill: textColor, stroke: textStroke } = pickTextAndStroke(color);
    const { canvas, ctx, dpr } = makeCanvas(size, size);
    roundedRect(ctx, 2, 2, size - 4, size - 4, 6);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.font = '700 14px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = textStroke;
    ctx.strokeText(tempText, center, center + 4);
    ctx.fillStyle = textColor;
    ctx.fillText(tempText, center, center + 4);
    return { canvas, w: size, h: size, ax: center, ay: center, dpr };
}

// ---------------------------------------------------------------------------
// Popup HTML (port 1:1 da v1)
// ---------------------------------------------------------------------------

// wsBuoyPopup (map-markers.js:86-124)
function wsBuoyPopup(b) {
    const time = b.data ? new Date(b.data).toLocaleString('it-IT', IT_DDMM_HHMM) : '';
    const seaText = wsMarineMetricText(b.temp_mare, '°C');
    const direction = cleanWindDirection(b.wind_direction);
    const metrics = [
        wsMarineMetric('Onda', b.wave_height_m, 'm'),
        wsMarineMetric('Periodo', b.wave_period_s, 's', 0),
        b.wave_direction ? `<div class="ws-ndbc-popup__metric"><span>Dir. onda</span><strong>${esc(cleanWindDirection(b.wave_direction) || String(b.wave_direction))}</strong></div>` : '',
        wsMarineMetric('Vento', b.wind, 'km/h', 0),
        wsMarineMetric('Raffica', b.wind_gust, 'km/h', 0),
        direction ? `<div class="ws-ndbc-popup__metric"><span>Direzione</span><strong>${esc(direction)}</strong></div>` : '',
        wsMarineMetric('Idrometrico', b.idrometrico, 'm'),
    ].filter(Boolean).join('');

    return `<article class="ws-ndbc-popup">
        <header class="ws-ndbc-popup__head">
            <span class="ws-ndbc-popup__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" role="img" focusable="false">
                    <path d="M16 3l4 9h-8l4-9Z"/>
                    <path d="M11 12h10l-1.4 8h-7.2L11 12Z"/>
                    <path d="M6 24c2 0 2-1.1 4-1.1s2 1.1 4 1.1 2-1.1 4-1.1 2 1.1 4 1.1 2-1.1 4-1.1"/>
                    <path d="M6 28c2 0 2-1.1 4-1.1s2 1.1 4 1.1 2-1.1 4-1.1 2 1.1 4 1.1 2-1.1 4-1.1"/>
                </svg>
            </span>
            <div>
                <span class="ws-ndbc-popup__kicker">Boa ondametrica${b.provincia ? ' · ' + esc(b.provincia) : ''}</span>
                <h3>${esc(b.name || 'Boa')}</h3>
                ${time ? `<p class="ws-ndbc-popup__time">${esc(time)} ora italiana</p>` : ''}
            </div>
        </header>
        <section class="ws-ndbc-popup__hero">
            <span>Temperatura mare</span>
            <strong>${esc(seaText || 'n.d.')}</strong>
        </section>
        ${metrics ? `<div class="ws-ndbc-popup__grid">${metrics}</div>` : '<p class="ws-ndbc-popup__empty">Dati parziali</p>'}
    </article>`;
}

// wsNdbcShipPopup (map-markers.js:23-84)
function wsNdbcShipPopup(row) {
    const time = row.data_locale || (row.data ? new Date(row.data).toLocaleString('it-IT', IT_DDMM_HHMM) : '');
    const direction = cleanWindDirection(row.direzione);
    const seaText = wsMarineMetricText(row.temp_mare, '°C');
    const airText = wsMarineMetricText(row.temp_aria, '°C');
    const primaryValue = seaText || airText || 'NOAA';
    const primaryLabel = seaText ? 'Temperatura mare' : (airText ? 'Temperatura aria' : 'Osservazione nave');
    const windText = wsMarineMetricText(row.vento, 'km/h', 0);
    const pressureText = wsMarineMetricText(row.pressione, 'hPa', 0);
    const distanceKm = finiteValue(row.distance_nm);
    const distanceText = distanceKm === null ? '' : wsMarineMetricText(distanceKm * 1.852, 'km', 0);
    const visibilityKm = finiteValue(row.visibility_nm);
    const visibilityText = visibilityKm === null ? '' : wsMarineMetricText(visibilityKm * 1.852, 'km', 0);
    const metrics = [
        seaText && airText ? wsMarineMetric('Aria', row.temp_aria, '°C') : '',
        wsMarineMetric('Pressione', row.pressione, 'hPa', 0),
        wsMarineMetric('Vento', row.vento, 'km/h', 0),
        wsMarineMetric('Raffica', row.raffica, 'km/h', 0),
        direction ? `<div class="ws-ndbc-popup__metric"><span>Direzione</span><strong>${esc(direction)}</strong></div>` : '',
        wsMarineMetric('Onda', row.wave_height_m, 'm'),
        wsMarineMetric('Periodo', row.wave_period_s, 's', 0),
        wsMarineMetric('Dew point', row.dew_point, '°C'),
        visibilityText ? `<div class="ws-ndbc-popup__metric"><span>Visibilità</span><strong>${esc(visibilityText)}</strong></div>` : '',
    ].filter(Boolean).join('');
    const sourceUrl = sanitizeHttpUrl(row.page_url || 'https://www.ndbc.noaa.gov/radial_search.php?lat1=38&lon1=14&uom=E&dist=100&ot=A&time=1');

    return `<article class="ws-ndbc-popup">
        <header class="ws-ndbc-popup__head">
            <span class="ws-ndbc-popup__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" role="img" focusable="false">
                    <path d="M8 19h16l-2.7 5.1a4 4 0 0 1-3.5 2.1h-3.6a4 4 0 0 1-3.5-2.1L8 19Z"/>
                    <path d="M12 8h7.8l2.2 11H9.6L12 8Z"/>
                    <path d="M14 8V4h4v4"/>
                    <path d="M6 26c2 0 2-1.1 4-1.1s2 1.1 4 1.1 2-1.1 4-1.1 2 1.1 4 1.1 2-1.1 4-1.1"/>
                </svg>
            </span>
            <div>
                <span class="ws-ndbc-popup__kicker">NOAA NDBC</span>
                <h3>${esc(row.name || 'NDBC nave')}</h3>
                ${time ? `<p class="ws-ndbc-popup__time">${esc(time)} ora italiana · nave</p>` : '<p class="ws-ndbc-popup__time">Osservazione nave</p>'}
                <p class="ws-ndbc-popup__note">Posizione arrotondata a ~10 km (fonte NDBC)</p>
            </div>
        </header>
        <section class="ws-ndbc-popup__hero">
            <span>${esc(primaryLabel)}</span>
            <strong>${esc(primaryValue)}</strong>
        </section>
        <div class="ws-ndbc-popup__chips">
            ${pressureText ? `<span>${esc(pressureText)}</span>` : ''}
            ${windText ? `<span>${esc(windText)} vento</span>` : ''}
            ${distanceText ? `<span>Distanza ${esc(distanceText)}</span>` : ''}
        </div>
        ${metrics ? `<div class="ws-ndbc-popup__grid">${metrics}</div>` : '<p class="ws-ndbc-popup__empty">Dati parziali</p>'}
        <a class="ws-ndbc-popup__link" href="${esc(sourceUrl)}" target="_blank" rel="noopener">Fonte NOAA</a>
    </article>`;
}

// ascatFmt/ascatPopup (map-derived-layers.js:643-664): obs_time GIÀ Europe/Rome.
function ascatFmt(s) {
    const m = String(s || '').match(/^(\d{4})-(\d\d)-(\d\d)[ T](\d\d):(\d\d)/);
    return m ? `${m[3]}/${m[2]} ${m[4]}:${m[5]}` : 'n.d.';
}
function ascatPopup(p, ms, dirTo) {
    const kmh = ms * 3.6;
    const dirFrom = (dirTo + 180) % 360;
    const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
    const card = COMPASS[Math.round(dirFrom / 45) % 8];
    const prod = p.product === 'coastal' ? 'costiero 12.5 km' : '25 km';
    return `
        <article class="ws-temp-variation-popup">
            <span>Vento mare · ${esc(p.satellite)} (ASCAT)</span>
            <h3>${kmh.toFixed(0)} km/h<small>${ms.toFixed(1)} m/s</small></h3>
            <dl>
                <div><dt>Provenienza</dt><dd>da ${card} (${dirFrom.toFixed(0)}°)</dd></div>
                <div><dt>Risoluzione</dt><dd>${prod}</dd></div>
                <div><dt>Passaggio</dt><dd>${esc(ascatFmt(p.time))}</dd></div>
            </dl>
            <small style="opacity:.7">Basato su EUMETSAT OSI SAF ASCAT winds</small>
        </article>`;
}

// s1Popup (map-derived-layers.js:749-765)
function s1Popup(sat, tl, ms, dirTo) {
    const kmh = ms * 3.6;
    const dirFrom = (dirTo + 180) % 360;
    const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
    const card = COMPASS[Math.round(dirFrom / 45) % 8];
    return `
        <article class="ws-temp-variation-popup">
            <span>Vento mare HD · ${esc(sat || 'Sentinel-1')} (SAR)</span>
            <h3>${kmh.toFixed(0)} km/h<small>${ms.toFixed(1)} m/s</small></h3>
            <dl>
                <div><dt>Provenienza</dt><dd>da ${card} (${dirFrom.toFixed(0)}°)</dd></div>
                <div><dt>Risoluzione</dt><dd>~1 km (radar)</dd></div>
                <div><dt>Passaggio</dt><dd>${esc(tl || 'n.d.')}</dd></div>
            </dl>
            <small style="opacity:.7">Contains modified Copernicus Sentinel data</small>
        </article>`;
}

// ---------------------------------------------------------------------------
// Webcam: timelapse helper (port di openWebcamTimelapse, map-webcams.js:66-88;
// stesso global della v1, usato dal bottone inline del popup)
// ---------------------------------------------------------------------------
async function openWebcamTimelapse(slug, fallbackUrl) {
    const safeFallback = sanitizeHttpUrl(fallbackUrl || '');
    if (!slug) {
        if (safeFallback) window.open(safeFallback, '_blank', 'noopener');
        return;
    }
    try {
        const response = await fetch(`${CAM_SNAPSHOT_BASE}${encodeURIComponent(slug)}/archive`, {
            method: 'GET', headers: { Accept: 'application/json' }, credentials: 'omit', cache: 'no-store',
        });
        const payload = await response.json();
        const item = Array.isArray(payload?.timelapseItems) ? payload.timelapseItems[0] : null;
        const url = sanitizeHttpUrl(item?.url ? new URL(item.url, 'https://cam.weathersicily.it').href : '');
        window.open(url || safeFallback || `https://cam.weathersicily.it/cam/${encodeURIComponent(slug)}#timelapse`, '_blank', 'noopener');
    } catch (error) {
        window.open(safeFallback || `https://cam.weathersicily.it/cam/${encodeURIComponent(slug)}#timelapse`, '_blank', 'noopener');
    }
}
window.wsOpenWebcamTimelapse = openWebcamTimelapse;

// Popup webcam (port 1:1 di map-webcams.js:169-253, inline styles identici)
function webcamPopupHtml(w, thumb, link, timelapseLink, slug) {
    const safeName = esc(w.name || 'Webcam');
    const safeSlug = esc(slug);
    const safeProvincia = esc(w.provincia || '');
    const safeRegion = esc(w.region || '');
    const status = (typeof w.status !== 'undefined') ? Number(w.status) : 1;
    const shotTime = esc(formatWebcamShotTime(w.snapshot_updated_at || w.last_update || ''));
    const visionLabel = esc(w.snapshot_vision_label || '');
    const statusBadge = status === 0
        ? `<span style="font-size:11px;color:#ff5252;margin-left:6px;">• offline</span>`
        : `<span style="font-size:11px;color:#a0ffb7;margin-left:6px;">• online</span>`;

    let popupHtml = `
  <div style="
    width:320px;
    max-width:100%;
    padding:8px;
    border-radius:12px;
    background:rgba(5,7,11,0.96);
    border:1px solid rgba(255,255,255,0.12);
    box-shadow:0 10px 25px rgba(0,0,0,0.55);
    backdrop-filter:blur(10px);
    -webkit-backdrop-filter:blur(10px);
    color:#e8ebf0;
    font-family:Manrope,system-ui,-apple-system,Segoe UI,Roboto;
    -webkit-font-smoothing:antialiased;
  ">
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <div style="
        width:20px;height:20px;border-radius:6px;
        background:rgba(255,183,77,0.12);
        display:flex;align-items:center;justify-content:center;
        ">
        <span style="font-size:13px;">📷</span>
      </div>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
          ${safeName}${statusBadge}
        </div>
        ${safeProvincia ? `<div style="font-size:11px;color:#aab1bb;">${safeProvincia}${safeRegion ? ' • ' + safeRegion : ''}</div>` : ''}
        ${shotTime ? `<div style="font-size:11px;color:#cbd5df;">Ultimo scatto ${shotTime}${visionLabel ? ' • ' + visionLabel : ''}</div>` : ''}
      </div>
    </div>
`;
    if (thumb) {
        popupHtml += `
    <div style="
      border-radius:10px;
      overflow:hidden;
      margin-bottom:6px;
      border:1px solid rgba(255,255,255,0.12);
      background:#05070b;
    ">
      <img src="${thumb}"
           loading="lazy"
           data-fallback="${esc(sanitizeHttpUrl(w.thumbnail || w.representative_url || ''))}"
           alt="${safeName}"
           style="width:100%;display:block;object-fit:cover;max-height:170px;"
           onerror="if(this.dataset.fallback){this.src=this.dataset.fallback;this.dataset.fallback='';this.title='Copertina della webcam';}else{this.style.display='none';}">
    </div>
  `;
    }
    if (link) {
        popupHtml += `
    <div style="display:flex;flex-wrap:wrap;gap:7px;align-items:center;">
      <a href="${link}" target="_blank" rel="noopener"
         style="display:inline-flex;align-items:center;gap:6px;
              font-size:12px;color:#86e1ff;text-decoration:none;
              padding:4px 8px;border-radius:999px;
              background:rgba(134,225,255,0.06);
              border:1px solid rgba(134,225,255,0.28);">
        <span>Apri live / dettagli</span>
        <span>↗</span>
      </a>
      ${slug ? `<button type="button" onclick="window.wsOpenWebcamTimelapse(this.dataset.slug, this.dataset.fallback)" data-slug="${safeSlug}" data-fallback="${timelapseLink}"
         style="display:inline-flex;align-items:center;gap:6px;
              font-size:12px;color:#c7f9ff;text-decoration:none;
              padding:4px 8px;border-radius:999px;
              background:rgba(45,212,191,0.08);
              border:1px solid rgba(45,212,191,0.28);
              font-family:inherit;cursor:pointer;">
        <span>Timelapse</span>
        <span>↗</span>
      </button>` : ''}
    </div>
  `;
    }
    if (!link && !thumb) {
        popupHtml += `
    <div style="font-size:11px;color:#aab1bb;">
      Nessun contenuto disponibile per questa webcam.
    </div>
  `;
    }
    popupHtml += `</div>`;
    return popupHtml;
}

function formatWebcamShotTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('it-IT', IT_DDMM_HHMM);
}

function webcamSlugValue(w) {
    return String(w?.slug || w?.container_name || w?.id || '').trim();
}

function webcamTimelapseFallback(w) {
    return sanitizeHttpUrl(w?.timelapse_url || w?.link || w?.url || '');
}

// Tooltip MeteoTracker (port dell'HTML v1, map-markers.js:3024-3035)
function mobileTooltipHtml(safeName, safeTime, t) {
    return `
  <div class="ws-station-tooltip ws-mobile-tooltip">
    <div class="ws-station-tooltip__top">
      <span class="ws-station-tooltip__badge">MeteoTracker</span>
      ${safeTime ? `<span class="ws-station-tooltip__time">${safeTime}</span>` : ''}
    </div>
    <div class="ws-station-tooltip__name">${safeName}</div>
    <div class="ws-station-tooltip__value">
      <strong>${t.toFixed(1)}</strong>
      <span>temperatura</span>
    </div>
  </div>`.trim();
}

// ---------------------------------------------------------------------------
// CSS v1 portato (map.css, valori ESATTI; shell Leaflet → MapLibre)
// ---------------------------------------------------------------------------
const MARINE_EXTRA_CSS = `
/* shell popup (v1: .leaflet-popup-content-wrapper → .maplibregl-popup-content) */
.ws-ndbc-popup-shell .maplibregl-popup-content{padding:0;margin:0;border-radius:14px;overflow:hidden;box-shadow:0 18px 44px rgba(8,26,42,0.22),0 3px 12px rgba(8,26,42,0.12)}
.ws-temp-variation-popup-shell .maplibregl-popup-content{padding:0;margin:0;border-radius:14px;overflow:hidden}
.ws-etna-pop-shell,.ws-etna-pop-shell .maplibregl-popup-content{pointer-events:auto}
.ws-etna-pop-shell .maplibregl-popup-content{padding:0;margin:0;border-radius:16px;max-height:min(560px,calc(100dvh - 96px));overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;border:1px solid rgba(127,127,127,.18);box-shadow:0 14px 44px rgba(0,0,0,.24);scrollbar-width:thin}
.ws-webcam-popup .maplibregl-popup-content{padding:0;margin:0;border-radius:18px;background:transparent;box-shadow:none;border:none}
.ws-webcam-popup .maplibregl-popup-tip{display:none}
/* NDBC/boe popup (map.css:6028-6203) */
.ws-ndbc-popup{color:#102033;font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;overflow:hidden;width:min(340px,calc(100vw - 44px))}
.ws-ndbc-popup__head{align-items:center;background:linear-gradient(135deg,#e8f7fb 0%,#f7fbfd 58%,#eef8ff 100%);border-bottom:1px solid rgba(16,32,45,0.08);display:grid;gap:10px;grid-template-columns:42px 1fr;padding:14px 15px 12px}
.ws-ndbc-popup__icon{align-items:center;background:#0f7ea7;border:2px solid rgba(255,255,255,0.95);border-radius:14px;box-shadow:0 10px 22px rgba(15,126,167,0.24);color:#fff;display:flex;height:42px;justify-content:center;width:42px}
.ws-ndbc-popup__icon svg{display:block;fill:none;height:28px;stroke:currentColor;stroke-linecap:round;stroke-linejoin:round;stroke-width:2;width:28px}
.ws-ndbc-popup__kicker{color:#0f7ea7;display:block;font-size:10px;font-weight:900;letter-spacing:.08em;line-height:1;text-transform:uppercase}
.ws-ndbc-popup h3{color:#07131d;font-size:16px;font-weight:850;letter-spacing:0;line-height:1.16;margin:4px 0 3px}
.ws-ndbc-popup__time{color:#647486;font-size:12px;font-weight:650;line-height:1.25;margin:0}
.ws-ndbc-popup__note{color:#8a97a6;font-size:10.5px;font-weight:600;font-style:italic;line-height:1.2;margin:2px 0 0}
.ws-ndbc-popup__hero{align-items:end;background:#fff;border-bottom:1px solid rgba(16,32,45,0.08);display:flex;justify-content:space-between;gap:12px;padding:13px 15px 12px}
.ws-ndbc-popup__hero span{color:#5f7184;font-size:12px;font-weight:800}
.ws-ndbc-popup__hero strong{color:#07131d;font-size:28px;font-weight:900;letter-spacing:0;line-height:.95}
.ws-ndbc-popup__chips{display:flex;flex-wrap:wrap;gap:6px;padding:10px 15px 0}
.ws-ndbc-popup__chips span{background:rgba(15,126,167,0.08);border:1px solid rgba(15,126,167,0.16);border-radius:999px;color:#23657d;font-size:11px;font-weight:800;line-height:1;padding:6px 8px}
.ws-ndbc-popup__grid{display:grid;gap:7px;grid-template-columns:repeat(2,minmax(0,1fr));padding:11px 15px 13px}
.ws-ndbc-popup__metric{background:#f6f9fb;border:1px solid rgba(16,32,45,0.08);border-radius:10px;min-width:0;padding:8px 9px}
.ws-ndbc-popup__metric span{color:#718094;display:block;font-size:10px;font-weight:800;line-height:1.1}
.ws-ndbc-popup__metric strong{color:#10202d;display:block;font-size:14px;font-weight:850;line-height:1.1;margin-top:4px}
.ws-ndbc-popup__empty{color:#647486;font-size:13px;font-weight:700;margin:0;padding:12px 15px 14px}
.ws-ndbc-popup__link{align-items:center;background:#10202d;color:#fff;display:flex;font-size:12px;font-weight:850;justify-content:center;min-height:40px;text-decoration:none}
.ws-ndbc-popup__link:hover{background:#0f7ea7;color:#fff}
/* popup ASCAT/S1 (map.css:1902-1963) */
.ws-temp-variation-popup{width:250px;padding:14px;color:#102033;font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial}
.ws-temp-variation-popup>span{color:#0079b8;font-size:10px;font-weight:850;letter-spacing:.08em;text-transform:uppercase}
.ws-temp-variation-popup h3{margin:4px 0 10px;font-size:16px;line-height:1.15}
.ws-temp-variation-popup strong{display:block;margin-bottom:10px;font-size:32px;line-height:1;font-weight:900}
.ws-temp-variation-popup small{margin-left:2px;font-size:15px}
.ws-temp-variation-popup dl{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:7px;margin:0}
.ws-temp-variation-popup dl div{min-width:0;padding:8px;border-radius:10px;background:rgba(16,32,45,0.055)}
.ws-temp-variation-popup dt,.ws-temp-variation-popup dd{margin:0}
.ws-temp-variation-popup dt{color:#5b7080;font-size:10px;font-weight:800}
.ws-temp-variation-popup dd{margin-top:3px;font-size:13px;font-weight:850}
/* popup aeroporti/ICON-2I (map.css:7597-7614) */
.ws-etna-pop{width:252px;color:#102033;font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;font-size:.86rem;line-height:1.42}
.ws-etna-pop__top{display:flex;gap:.6rem;align-items:center;padding:13px 14px;background:linear-gradient(135deg,rgba(0,121,184,.16),rgba(0,121,184,.03));border-bottom:1px solid rgba(127,127,127,.14)}
.ws-etna-pop__top h3{margin:0 0 2px;font-size:.95rem;font-weight:800}
.ws-etna-pop__top strong{font-size:.85rem;color:#0079b8}
.ws-etna-pop__dot{width:18px;height:18px;border-radius:50%;flex:0 0 auto;box-shadow:0 0 0 2px #fff,0 1px 4px rgba(0,0,0,.35)}
.ws-etna-pop__list{list-style:none;margin:0;padding:12px 14px;display:flex;flex-direction:column;gap:.65rem}
.ws-etna-pop__list li{display:flex;gap:.55rem;align-items:flex-start}
.ws-etna-pop__ic{font-size:1.2rem;flex:0 0 auto;line-height:1.2}
.ws-etna-pop__list b{font-weight:700}
.ws-etna-pop__list small{opacity:.55}
.ws-etna-legend__gradient{display:inline-block;width:58px;height:7px;border-radius:999px;background:linear-gradient(90deg,#c65ad4,#536bd2,#55c9dc,#43c84a,#efd82e,#e85a32);vertical-align:middle}
.ws-etna-pop__src{margin:0;padding:9px 14px;border-top:1px solid rgba(127,127,127,.16);font-size:.68rem;opacity:.6;background:rgba(127,127,127,.06)}
/* tooltip MeteoTracker: blocco valore (map.css:4279-4300; il resto è già in app.css) */
.ws-station-tooltip__value{display:flex;align-items:baseline;gap:7px}
.ws-station-tooltip__value strong{color:var(--ws-fg,#07131d);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;font-size:22px;font-weight:800;line-height:1}
.ws-station-tooltip__value span{min-width:0;color:var(--ws-fg-soft,#627383);font-size:10px;font-weight:800;line-height:1;text-transform:uppercase}
`;

function injectCss() {
    if (document.getElementById('wsv2-marine-extra-css')) return;
    const style = document.createElement('style');
    style.id = 'wsv2-marine-extra-css';
    style.textContent = MARINE_EXTRA_CSS;
    document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Stato + infrastruttura symbol layer
// ---------------------------------------------------------------------------
const EMPTY = { type: 'FeatureCollection', features: [] };

const state = {
    map: null,
    timer: null,
    hoverPopup: null,
    clickPopup: null,
    sub: {}, // key → { enabled, seq, rows/dati specifici }
};

function srcId(key) { return 'ws-mx-' + key + '-src'; }
function lyrId(key) { return 'ws-mx-' + key; }

// Pane v1 → ordine v2: vento mare sotto i simboli stazione (ascat 624, s1 625),
// marker live sopra (boe/ndbc/mobile/webcam: weatherMarkerPane
// 649 con zIndexOffset ~1000, cioè sopra le stazioni).
const LAYER_OPTS = {
    boe:       { below: false },
    ndbcShips: { below: false },
    mobile:    { below: false },
    webcam:    { below: false },
    ascat:     { below: true, rotate: true },
    s1wind:    { below: true, rotate: true },
};

function ensureLayer(key) {
    const map = state.map;
    if (map.getSource(srcId(key))) return true;
    if (!isMapStyleReady(map)) {
        const sub = state.sub[key];
        if (!sub._pendingIdle) {
            sub._pendingIdle = true;
            map.once('idle', () => {
                sub._pendingIdle = false;
                if (sub.enabled && sub.rerender) sub.rerender();
            });
        }
        return false;
    }
    const opts = LAYER_OPTS[key];
    map.addSource(srcId(key), { type: 'geojson', data: EMPTY });
    // Connettori declutter boe: linea di richiamo + puntino SOTTO le boe
    // (v1 pane boeDeclutterPane 615 < weatherMarkerPane 649).
    if (key === 'boe') {
        map.addSource('ws-mx-boe-conn-src', { type: 'geojson', data: EMPTY });
        map.addLayer({
            id: 'ws-mx-boe-conn-line', type: 'line', source: 'ws-mx-boe-conn-src',
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
                // v1: polyline color #0b5cad weight 1.5 opacity .7 dashArray '3 3'
                'line-color': '#0b5cad', 'line-width': 1.5, 'line-opacity': 0.7,
                'line-dasharray': [2, 2], // 3px/width1.5 → unità di larghezza
            },
        });
        map.addLayer({
            id: 'ws-mx-boe-conn-dot', type: 'circle', source: 'ws-mx-boe-conn-src',
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
                // v1: circleMarker r2.5, bordo #0b5cad 1.5, fill bianco pieno
                'circle-radius': 2.5, 'circle-color': '#ffffff', 'circle-opacity': 1,
                'circle-stroke-color': '#0b5cad', 'circle-stroke-width': 1.5,
            },
        });
    }
    const layout = {
        'icon-image': ['get', 'sk'],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'symbol-z-order': 'viewport-y',
        'symbol-sort-key': ['get', 'y'],
    };
    if (opts.rotate) {
        layout['icon-rotate'] = ['get', 'dir'];
        layout['icon-rotation-alignment'] = 'map'; // nord geografico, come i divIcon v1
    }
    const before = opts.below && map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : undefined;
    map.addLayer({ id: lyrId(key), type: 'symbol', source: srcId(key), layout }, before);
    return true;
}

function setData(key, features) {
    const src = state.map.getSource(srcId(key));
    if (src) src.setData({ type: 'FeatureCollection', features });
}

function clearLayer(key) {
    setData(key, []);
    if (key === 'boe') {
        const conn = state.map.getSource('ws-mx-boe-conn-src');
        if (conn) conn.setData(EMPTY);
    }
}

// popup click condiviso (Leaflet bindPopup = click); shell/width/offset per layer
const POPUP_OPTS = {
    boe:       { className: 'ws-ndbc-popup-shell', maxWidth: '320px', offset: 18 },
    ndbcShips: { className: 'ws-ndbc-popup-shell', maxWidth: '360px', offset: 52 },
    ascat:     { className: 'ws-temp-variation-popup-shell', maxWidth: '260px', offset: 12 },
    s1wind:    { className: 'ws-temp-variation-popup-shell', maxWidth: '260px', offset: 12 },
    webcam:    { className: 'ws-webcam-popup', maxWidth: '320px', offset: 24 },
};

function wireInteractions(key) {
    const map = state.map;
    const lyr = lyrId(key);
    map.on('mousemove', lyr, (e) => {
        const f = e.features && e.features[0];
        if (!f) return;
        map.getCanvas().style.cursor = 'pointer';
        if (!f.properties.tip) return;
        state.hoverPopup
            .setLngLat(f.geometry.coordinates)
            .setHTML(f.properties.tip)
            .addTo(map);
    });
    map.on('mouseleave', lyr, () => {
        map.getCanvas().style.cursor = '';
        state.hoverPopup.remove();
    });
    map.on('click', lyr, (e) => {
        const f = e.features && e.features[0];
        if (!f || !f.properties.pop) return;
        state.hoverPopup.remove();
        if (state.clickPopup) state.clickPopup.remove();
        const opts = POPUP_OPTS[key] || {};
        state.clickPopup = new maplibregl.Popup({
            closeButton: true, closeOnClick: true,
            className: opts.className, maxWidth: opts.maxWidth, offset: opts.offset,
        }).setLngLat(f.geometry.coordinates).setHTML(f.properties.pop).addTo(map);
    });
}

// ---------------------------------------------------------------------------
// BOE (v1 drawBoe + declutter wsDeclutterBoe)
// ---------------------------------------------------------------------------
async function fetchBoe() {
    const sub = state.sub.boe;
    const seq = ++sub.seq;
    const data = await window.WSWeatherApi.getBuoys().catch(() => []);
    if (seq !== sub.seq || !sub.enabled) return;
    sub.rows = (Array.isArray(data) ? data : [])
        .map(b => {
            const lat = finiteValue(b.lat);
            const lon = finiteValue(b.lng);
            const temp = finiteValue(b.temp_mare);
            if (lat === null || lon === null || temp === null) return null;
            return { b, lat, lon, temp };
        })
        .filter(Boolean);
    renderBoe();
}

// Declutter boe⟷stazioni portato dalla v1 (map-markers.js:295-352): a OGNI
// zoom, la boa che si sovrappone (<34px) alla stazione più vicina viene
// spostata di 30px in direzione opposta, con lineetta tratteggiata + puntino
// bianco sul punto reale. Le posizioni stazione arrivano dal layer stations v2.
function renderBoe() {
    const sub = state.sub.boe;
    const map = state.map;
    if (!sub.enabled || !map || !ensureLayer('boe')) return;
    const rows = sub.rows || [];
    const declutter = map.getZoom() >= WS_BOE_DECLUTTER_MIN_ZOOM;
    const stationPts = declutter
        ? (stationsState.visible || [])
            .map(r => {
                const la = Number(r.lat), ln = Number(r.lng ?? r.lon);
                if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
                return map.project([ln, la]);
            })
            .filter(Boolean)
        : [];

    const features = [];
    const connectors = [];
    rows.forEach(({ b, lat, lon, temp }) => {
        const color = seaTempScale(temp).hex();
        const key = `mx-boe|${temp.toFixed(1)}|${color}`;
        addImageOnce(map, key, () => buildBuoySprite(temp.toFixed(1), color));

        let coords = [lon, lat];
        if (stationPts.length) {
            const truePt = map.project([lon, lat]);
            let nearest = null, nd = Infinity;
            for (const sp of stationPts) {
                const d = Math.hypot(truePt.x - sp.x, truePt.y - sp.y);
                if (d < nd) { nd = d; nearest = sp; }
            }
            if (nearest && nd < WS_BOE_OVERLAP_PX) {
                // Direzione di fuga: parte da "lontano dalla stazione più
                // vicina" ma PROVA più angoli e sceglie quello che massimizza
                // la distanza da TUTTE le stazioni (con due stazioni vicine,
                // es. Stromboli+Ginostra, la fuga cieca atterrava sulla seconda).
                let vx = truePt.x - nearest.x, vy = truePt.y - nearest.y;
                const len = Math.hypot(vx, vy);
                if (len < 0.5) { vx = 0; vy = -1; }
                else { vx /= len; vy /= len; }
                const base = Math.atan2(vy, vx);
                let best = { score: -Infinity, x: 0, y: 0 };
                for (const dA of [0, 0.7854, -0.7854, 1.5708, -1.5708, 2.3562, -2.3562, 3.1416]) {
                    const a = base + dA;
                    const cx2 = truePt.x + Math.cos(a) * WS_BOE_OFFSET_PX;
                    const cy2 = truePt.y + Math.sin(a) * WS_BOE_OFFSET_PX;
                    let minD = Infinity;
                    for (const sp of stationPts) {
                        const d = Math.hypot(cx2 - sp.x, cy2 - sp.y);
                        if (d < minD) minD = d;
                    }
                    // preferenza leggera per la direzione naturale (dA=0)
                    const score = minD - Math.abs(dA) * 2;
                    if (score > best.score) best = { score, x: cx2, y: cy2 };
                    if (minD >= 52 && dA === 0) { best = { score: Infinity, x: cx2, y: cy2 }; break; }
                }
                vx = (best.x - truePt.x) / WS_BOE_OFFSET_PX;
                vy = (best.y - truePt.y) / WS_BOE_OFFSET_PX;
                const off = map.unproject([best.x, best.y]);
                coords = [off.lng, off.lat];
                // Linea BORDO-A-BORDO (feedback Giuseppe): dal bordo del marker
                // stazione (~r24 con pillola) al bordo del chip boa (~17 di
                // mezza altezza), MAI centro-a-centro che cancella i valori.
                const EDGE_STATION = 26, EDGE_BUOY = 19;
                const p1 = map.unproject([truePt.x + vx * EDGE_STATION, truePt.y + vy * EDGE_STATION]);
                const p2 = map.unproject([truePt.x + vx * (WS_BOE_OFFSET_PX - EDGE_BUOY), truePt.y + vy * (WS_BOE_OFFSET_PX - EDGE_BUOY)]);
                connectors.push({
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: [[p1.lng, p1.lat], [p2.lng, p2.lat]] },
                    properties: {},
                });
                // puntino sul BORDO del marker (non sul centro: coprirebbe il valore)
                connectors.push({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [p1.lng, p1.lat] },
                    properties: {},
                });
            }
        }

        const safeName = esc(b.name || 'Boa');
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: coords },
            properties: {
                sk: key,
                y: lat * -1,
                tip: `<div class="ws-station-tooltip"><div class="ws-station-tooltip__name">${safeName}: ${temp.toFixed(1)}°C</div></div>`,
                pop: wsBuoyPopup(b),
            },
        });
    });
    setData('boe', features);
    const conn = map.getSource('ws-mx-boe-conn-src');
    if (conn) conn.setData({ type: 'FeatureCollection', features: connectors });
}

// ---------------------------------------------------------------------------
// NAVI NDBC (v1 drawNdbcShips)
// ---------------------------------------------------------------------------
async function fetchNdbcShips() {
    const sub = state.sub.ndbcShips;
    const seq = ++sub.seq;
    const data = await window.WSWeatherApi.getNdbcShipObservations().catch(() => []);
    if (seq !== sub.seq || !sub.enabled) return;
    sub.rows = Array.isArray(data) ? data : [];
    renderNdbcShips();
}

function renderNdbcShips() {
    const sub = state.sub.ndbcShips;
    const map = state.map;
    if (!sub.enabled || !map || !ensureLayer('ndbcShips')) return;
    const features = [];
    (sub.rows || []).forEach(row => {
        const lat = finiteValue(row.lat);
        const lon = finiteValue(row.lng);
        if (lat === null || lon === null) return;
        const seaTemp = finiteValue(row.temp_mare);
        const airTemp = finiteValue(row.temp_aria);
        const label = seaTemp !== null ? `${seaTemp.toFixed(1)}°` : (airTemp !== null ? `${airTemp.toFixed(1)}°` : 'NOAA');
        const color = seaTemp !== null ? seaTempScale(seaTemp).hex() : (airTemp !== null ? getColorScale('temperatura')(airTemp).hex() : '#0f7ea7');
        const key = `mx-ndbc|${label}|${color}`;
        addImageOnce(map, key, () => buildNdbcShipSprite(label, color));
        const safeName = esc(row.name || 'NOAA NDBC');
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lon, lat] },
            properties: {
                sk: key,
                y: lat * -1,
                tip: `<div class="ws-station-tooltip"><div class="ws-station-tooltip__name">${safeName}: ${esc(label)}</div></div>`,
                pop: wsNdbcShipPopup(row),
            },
        });
    });
    setData('ndbcShips', features);
}

// ---------------------------------------------------------------------------
// ASCAT (v1 activateAscatLayer/renderAscat): fusione passaggi 24h con dedup
// su griglia 0.1° tenendo il dato più fresco per cella.
// ---------------------------------------------------------------------------
async function fetchJson(url, timeoutMs = 12000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const r = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return await r.json();
    } finally { clearTimeout(timer); }
}

function arrowFeatures(map, nodesWithMeta) {
    // nodesWithMeta: [{lat, lon, ms, dirTo, tip, pop}]
    const features = [];
    nodesWithMeta.forEach(n => {
        const color = ascatColor(n.ms);
        const len = Math.min(11, 5 + n.ms * 0.55);
        const lenQ = Math.round(len * 2) / 2; // sprite per (colore, len 0.5px)
        const key = `mx-arrow|${color}|${lenQ}`;
        addImageOnce(map, key, () => buildArrowSprite(color, lenQ));
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [n.lon, n.lat] },
            properties: { sk: key, dir: n.dirTo, y: n.lat * -1, tip: n.tip, pop: n.pop },
        });
    });
    return features;
}

async function activateAscat() {
    const sub = state.sub.ascat;
    const seq = ++sub.seq;
    try {
        const data = await fetchJson(ASCAT_URL);
        if (seq !== sub.seq || !sub.enabled) return;
        const passes = Array.isArray(data.passes) ? data.passes : [];
        if (!passes.length) { console.warn('[wsv2] ASCAT: nessun passaggio recente'); return; }
        if (!ensureLayer('ascat')) { sub.rerenderData = () => renderAscatPasses(passes); return; }
        renderAscatPasses(passes);
    } catch (e) {
        console.warn('[wsv2] Vento mare ASCAT non disponibile:', e);
    }
}

function renderAscatPasses(passes) {
    const map = state.map;
    const seen = new Set();
    const nodes = [];
    passes.forEach(p => {
        (p.nodes || []).forEach(n => {
            const lat = finiteValue(n[0]), lon = finiteValue(n[1]), ms = finiteValue(n[2]), dirTo = finiteValue(n[3]);
            if (lat === null || lon === null || ms === null || dirTo === null) return;
            const cell = Math.round(lat / 0.1) + ',' + Math.round(lon / 0.1);
            if (seen.has(cell)) return; // passaggi ordinati dal più recente
            seen.add(cell);
            nodes.push({
                lat, lon, ms, dirTo,
                tip: `<div class="ws-station-tooltip"><div class="ws-station-tooltip__name">${(ms * 3.6).toFixed(0)} km/h</div></div>`,
                pop: ascatPopup(p, ms, dirTo),
            });
        });
    });
    setData('ascat', arrowFeatures(map, nodes));
}

// ---------------------------------------------------------------------------
// SENTINEL-1 (v1 activateS1Layer/renderS1): mosaico già deduplicato lato
// server; nodi [lat, lon, m/s, dir_verso, "DD/MM HH:MM", satellite].
// ---------------------------------------------------------------------------
async function activateS1() {
    const sub = state.sub.s1wind;
    const seq = ++sub.seq;
    try {
        const data = await fetchJson(S1_URL);
        if (seq !== sub.seq || !sub.enabled) return;
        const rawNodes = Array.isArray(data.nodes) ? data.nodes : [];
        if (!rawNodes.length) { console.warn('[wsv2] Sentinel-1: nessun dato recente'); return; }
        if (!ensureLayer('s1wind')) { sub.rerenderData = () => renderS1Nodes(rawNodes); return; }
        renderS1Nodes(rawNodes);
    } catch (e) {
        console.warn('[wsv2] Vento mare Sentinel-1 non disponibile:', e);
    }
}

function renderS1Nodes(rawNodes) {
    const map = state.map;
    const nodes = [];
    rawNodes.forEach(n => {
        const lat = finiteValue(n[0]), lon = finiteValue(n[1]), ms = finiteValue(n[2]), dirTo = finiteValue(n[3]);
        if (lat === null || lon === null || ms === null || dirTo === null) return;
        nodes.push({
            lat, lon, ms, dirTo,
            tip: `<div class="ws-station-tooltip"><div class="ws-station-tooltip__name">${(ms * 3.6).toFixed(0)} km/h</div></div>`,
            pop: s1Popup(n[5], n[4], ms, dirTo),
        });
    });
    setData('s1wind', arrowFeatures(map, nodes));
}

// ---------------------------------------------------------------------------
// WEBCAM (v1 drawWebcams): sprite = miniatura live (caricata con CORS) o
// glyph camera. I gruppi mantengono coordinate reali e si separano con lo zoom.
// ---------------------------------------------------------------------------
function loadImage(url, timeoutMs = 4500) {
    return new Promise((resolve, reject) => {
        const im = new Image();
        const timer = setTimeout(() => {
            im.onload = null;
            im.onerror = null;
            reject(new Error('image timeout'));
        }, timeoutMs);
        im.crossOrigin = 'anonymous';
        im.onload = () => { clearTimeout(timer); resolve(im); };
        im.onerror = (e) => { clearTimeout(timer); reject(e); };
        im.src = url;
    });
}

async function fetchWebcams() {
    const sub = state.sub.webcam;
    const seq = ++sub.seq;
    const rows = await window.WSWeatherApi.getWebcams().catch(() => []);
    if (seq !== sub.seq || !sub.enabled) return;
    if (!Array.isArray(rows)) return;

    const items = [];
    for (const w of [...rows].sort((a, b) => {
        const aKey = `${webcamSlugValue(a)}|${a?.name || ''}|${a?.lat}|${a?.lng}|${a?.url || ''}`;
        const bKey = `${webcamSlugValue(b)}|${b?.name || ''}|${b?.lat}|${b?.lng}|${b?.url || ''}`;
        return aKey.localeCompare(bKey, 'it');
    })) {
        const lat = finiteValue(w.lat);
        const lon = finiteValue(w.lng);
        if (lat === null || lon === null) continue;
        const slug = webcamSlugValue(w);
        // v1: thumb = w.thumb || w.image; per il popup vale lo snapshot live
        const thumb = sanitizeHttpUrl(w.thumb || w.image || (slug ? `${CAM_SNAPSHOT_BASE}${encodeURIComponent(slug)}/latest.jpg` : ''));
        items.push({ w, lat, lon, slug, thumb, key: 'mx-cam-glyph' });
    }

    const map = state.map;
    if (!ensureLayer('webcam')) {
        sub.rerenderData = () => fetchWebcams();
        return;
    }

    const featureFor = (it, coordinates) => {
        const { w, lat, slug, thumb, key } = it;
        const link = esc(sanitizeHttpUrl(w.url || w.link || ''));
        const timelapseLink = esc(webcamTimelapseFallback(w));
        return {
            type: 'Feature',
            geometry: { type: 'Point', coordinates },
            properties: {
                sk: key,
                y: lat * -1,
                tip: `<div class="ws-station-tooltip"><div class="ws-station-tooltip__name">${esc(w.name || 'Webcam')}</div></div>`,
                pop: webcamPopupHtml(w, esc(thumb), link, timelapseLink, slug),
            },
        };
    };

    const previews = new Map();
    const pending = new Set();
    const queue = [];
    let loading = 0;
    const loadPreview = (it) => {
        if (previews.has(it) || pending.has(it)) return;
        pending.add(it);
        queue.push(it);
        pump();
    };
    const pump = () => {
        while (loading < 4 && queue.length && seq === sub.seq && sub.enabled) {
            const it = queue.shift();
            loading++;
            (async () => {
                let image = null;
                for (const url of [...new Set([it.thumb, sanitizeHttpUrl(it.w.thumbnail || it.w.representative_url || '')])].filter(Boolean)) {
                    try { image = await loadImage(url, 8000); break; } catch (_) { /* Try cover. */ }
                }
                previews.set(it, image);
            })().finally(() => { loading--; renderItems(); pump(); });
        }
    };
    const renderItems = () => {
        if (seq !== sub.seq || !sub.enabled || !state.map?.getSource(srcId('webcam'))) return;
        const screenPoints = items.map((it, index) => {
            const point = state.map.project([it.lon, it.lat]);
            return { id: it.slug || `${it.w?.name || 'webcam'}-${index}`, x: point.x, y: point.y, it };
        });
        const canvas = state.map.getCanvas();
        const placed = layoutWebcamPoints(screenPoints.filter(p =>
            p.x >= -80 && p.y >= -80 && p.x <= canvas.clientWidth + 80 && p.y <= canvas.clientHeight + 80));
        setData('webcam', placed.map(point => {
            const it = point.it;
            loadPreview(it);
            const preview = previews.get(it);
            if (preview) {
                it.key = `mx-cam|${it.slug || it.thumb}|${point.members.length}`;
                const sprite = centeredImage(buildWebcamThumbSprite(preview, point.members.length));
                if (map.hasImage(it.key)) map.updateImage(it.key, sprite.data);
                else map.addImage(it.key, sprite.data, { pixelRatio: sprite.pixelRatio });
            }
            const feature = featureFor(it, [it.lon, it.lat]);
            if (point.members.length > 1) {
                feature.properties.tip = `<div class="ws-station-tooltip">${point.members.length} webcam vicine · clicca per scegliere</div>`;
                feature.properties.pop = `<div style="max-height:65vh;overflow-y:auto;background:#102434;color:white;padding:12px;border-radius:16px"><strong>${point.members.length} webcam in questa zona</strong>${point.members.map(p => `<div style="margin-top:12px">${featureFor(p.it, [p.it.lon, p.it.lat]).properties.pop}</div>`).join('')}</div>`;
            }
            return feature;
        }));
    };
    sub.renderItems = renderItems;

    // Prima passata immediata: glyph camera per tutte le webcam. Le miniature live
    // arrivano dopo, ma una snapshot lenta/rotta non blocca più l'intero layer.
    addImageOnce(map, 'mx-cam-glyph', () => buildWebcamGlyphSprite());
    renderItems();

    // Only visible representatives load previews, with bounded concurrency.
}

// ---------------------------------------------------------------------------
// METEOTRACKER (v1 drawMobile): quadratini temperatura, tooltip su hover.
// ---------------------------------------------------------------------------
function formatLocalTimeFromUTC(time_utc) {
    if (!time_utc) return '';
    let d;
    if (typeof time_utc === 'number') {
        d = new Date(time_utc > 1e12 ? time_utc : time_utc * 1000);
    } else {
        d = new Date(time_utc);
    }
    if (isNaN(d)) return '';
    return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Rome',
    }).format(d);
}

let mobileHours = 6;
function mobileStatus(extra = {}) {
    const sub = state.sub.mobile;
    document.dispatchEvent(new CustomEvent('wsv2:meteotracker', { detail: {
        enabled: !!sub?.enabled, hours: mobileHours, count: sub?.rows?.length || 0, ...extra,
    } }));
}

export function setMobileHours(hours) {
    if (![6, 12, 24, 36].includes(Number(hours))) return;
    mobileHours = Number(hours);
    if (state.sub.mobile?.enabled) fetchMobile();
}

async function fetchMobile() {
    const sub = state.sub.mobile;
    const seq = ++sub.seq;
    mobileStatus({ loading: true });
    try {
        const rows = await window.WSWeatherApi.getMobileStations(mobileHours);
        if (seq !== sub.seq || !sub.enabled) return;
        sub.rows = Array.isArray(rows) ? rows : [];
        renderMobile();
        mobileStatus();
    } catch {
        if (seq === sub.seq && sub.enabled) mobileStatus({ error: true });
    }
}

function renderMobile() {
    const sub = state.sub.mobile;
    const map = state.map;
    if (!sub.enabled || !map || !ensureLayer('mobile')) return;
    const features = [];
    (sub.rows || []).forEach(m => {
        const lat = finiteValue(m.lat);
        const lon = finiteValue(m.lng);
        const t = finiteValue(m.temperatura);
        if (lat === null || lon === null || t === null || !validStationValue('temperatura', t)) return;
        const color = liveTemperatureScale(t).hex();
        const text = (Math.round(t * 10) / 10).toFixed(1);
        const key = `mx-mobile|${text}|${color}`;
        addImageOnce(map, key, () => buildMobileSprite(text, color));
        const name = m.comune || m.city || m.name || 'Stazione';
        const safeName = esc(name);
        const safeTime = esc(formatLocalTimeFromUTC(m.time_utc));
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lon, lat] },
            properties: {
                sk: key,
                y: lat * -1,
                tip: mobileTooltipHtml(safeName, safeTime, t),
                pop: mobileTooltipHtml(safeName, safeTime, t),
            },
        });
    });
    setData('mobile', features);
}

// ---------------------------------------------------------------------------
// Registro attivazione + refresh
// ---------------------------------------------------------------------------
const DEFS = {
    boe:       { activate: fetchBoe,        refresh: fetchBoe },
    ndbcShips: { activate: fetchNdbcShips,  refresh: fetchNdbcShips },
    ascat:     { activate: activateAscat },
    s1wind:    { activate: activateS1 },
    webcam:    { activate: fetchWebcams,    refresh: fetchWebcams },
    mobile:    { activate: fetchMobile,     refresh: fetchMobile },
};

function refreshLive() {
    for (const key of LIVE_KEYS) {
        const sub = state.sub[key];
        if (sub && sub.enabled && DEFS[key].refresh) DEFS[key].refresh();
    }
}

// --- API pubblica ------------------------------------------------------------
export function initMarineExtra(map) {
    state.map = map;
    injectCss();
    for (const key of Object.keys(DEFS)) {
        state.sub[key] = { enabled: false, seq: 0, rows: null, rerender: null, rerenderData: null, _pendingIdle: false };
    }
    state.sub.boe.rerender = renderBoe;
    state.sub.ndbcShips.rerender = renderNdbcShips;
    state.sub.mobile.rerender = renderMobile;
    // Layer da file statico: se lo style non era pronto al fetch, il payload
    // viene ridisegnato appena la mappa è idle (rerenderData).
    for (const key of ['ascat', 's1wind', 'webcam']) {
        state.sub[key].rerender = () => {
            const sub = state.sub[key];
            if (sub.rerenderData && ensureLayer(key)) { sub.rerenderData(); sub.rerenderData = null; }
        };
    }
    state.hoverPopup = new maplibregl.Popup({
        closeButton: false, closeOnClick: false, offset: 12,
        className: 'wsv2-tooltip-shell', maxWidth: '280px',
    });
    for (const key of Object.keys(DEFS)) wireInteractions(key);

    // Declutter boe: ricalcolo a ogni cambio zoom (soglia in pixel) e quando
    // cambia la selezione stazioni (v1: hook zoomend + chiamate post-draw).
    map.on('zoomend', () => { if (state.sub.boe.enabled) renderBoe(); });
    map.on('moveend', () => {
        if (state.sub.webcam?.enabled && state.sub.webcam.renderItems) state.sub.webcam.renderItems();
    });
    document.addEventListener('wsv2:stations', () => { if (state.sub.boe.enabled) renderBoe(); });
    // DOPO ogni render stazioni (pan/zoom compresi): è QUI che la selezione
    // visibile è aggiornata — su zoomend si correva prima delle stazioni e il
    // declutter usava posizioni stantie (boa di Stromboli mai spostata).
    let boeSyncT = null;
    document.addEventListener('wsv2:stations-visible', () => {
        if (!state.sub.boe?.enabled) return;
        clearTimeout(boeSyncT);
        boeSyncT = setTimeout(renderBoe, 60);
    });

    // Cadenza live v1: refreshAll ogni 60s con guard document.hidden +
    // riallineamento immediato quando la tab torna visibile (map.js:1455-1462).
    state.timer = setInterval(() => { if (!document.hidden) refreshLive(); }, REFRESH_MS);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshLive(); });
}

export function setMarineExtraEnabled(key, on) {
    const def = DEFS[key];
    const sub = state.sub[key];
    if (!def || !sub || !state.map) return false;
    if (!!on === sub.enabled) return true;
    sub.enabled = !!on;
    if (key === 'mobile' && !sub.enabled) mobileStatus();
    if (sub.enabled) {
        def.activate();
    } else {
        sub.seq++; // invalida fetch in volo
        sub.rows = null;
        sub.rerenderData = null;
        if (state.map.getSource(srcId(key))) clearLayer(key);
        if (state.hoverPopup) state.hoverPopup.remove();
        if (state.clickPopup) { state.clickPopup.remove(); state.clickPopup = null; }
    }
    return true;
}
